import React from 'react';
import { formatExact } from '../utils/formatNumber';

/**
 * Tooltip común de los gráficos del tablero.
 *
 * Existe por dos motivos que el tooltip por defecto de recharts no cubre:
 *
 *  - <b>Muestra el valor EXACTO, sin abreviar.</b> El eje y las etiquetas abrevian
 *    porque no hay sitio; el tooltip es justamente donde el usuario va a buscar la
 *    cifra completa, y devolverle "1,25M" otra vez no le sirve de nada.
 *  - <b>Usa los tokens del tablero.</b> El de recharts trae fondo blanco cableado,
 *    así que en modo oscuro aparecía un rectángulo blanco sobre el gráfico.
 */
/** Una entrada del payload que recharts inyecta al tooltip. */
interface TooltipEntry {
  name?: string;
  value?: number | string;
  color?: string;
}

/**
 * Los tres primeros los inyecta recharts en tiempo de ejecución al pasar este
 * componente como `content`; se declaran opcionales porque el consumidor no los
 * escribe a mano. `TooltipProps` de recharts no se extiende aquí: su forma varía
 * entre versiones menores y encadenaba errores de tipo sin aportar seguridad real.
 */
interface ChartTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string | number;
  currency?: string | null;
  /** Total de la serie, para poder mostrar el porcentaje. */
  total?: number;
}

export const ChartTooltip: React.FC<ChartTooltipProps> = ({
  active,
  payload,
  label,
  currency = null,
  total = 0,
}) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div
      className="rounded-lg px-3 py-2 text-[12px] shadow-lg border"
      style={{
        background: 'var(--dash-surface-card)',
        borderColor: 'var(--dash-border)',
        color: 'var(--dash-text-primary)',
      }}
    >
      {label != null && label !== '' && (
        <p className="mb-1 font-medium" style={{ color: 'var(--dash-text-primary)' }}>
          {String(label)}
        </p>
      )}
      {payload.map((entry, index) => {
        /**
         * Cadena numérica incluida: hay endpoints que serializan todo como string y el resto
         * del módulo ya lo tolera (`KpiVentasHoy.toNum`, `ChartBarHorizontal`, `ChartDonut`).
         * Con el `: 0` anterior, esos gráficos se pintaban bien y su tooltip decía «$ 0».
         */
        const parsed = typeof entry.value === 'number'
          ? entry.value
          : Number(String(entry.value ?? '').replace(/,/g, ''));
        const value = Number.isFinite(parsed) ? parsed : 0;
        const share = total > 0 ? (value / total) * 100 : null;

        return (
          <div key={index} className="flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-sm flex-shrink-0"
              style={{ background: entry.color ?? 'var(--dash-accent)' }}
            />
            <span style={{ color: 'var(--dash-text-secondary)' }}>{entry.name}</span>
            <span className="ml-auto pl-3" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatExact(value, { currency })}
              {share !== null && (
                <span style={{ color: 'var(--dash-text-muted)' }}>{` · ${share.toFixed(0)} %`}</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
};
